import type { CheckboxGroupOption } from '@cvent/carina/components/Checkbox/CheckboxGroup/CheckboxGroup';
import dynamic from 'next/dynamic';
import { useCallback, useMemo, useRef, useState } from 'react';
import type { VenueDetailsOptions } from '@/utils/filters/options/getVenueDetailsOptions';
import type { Filter } from '@/models/Filter';
import Icon from '@/components/shared/Icons';

const Popper = dynamic(() => import('@cvent/carina/components/Popper'));

const CheckboxGroup = dynamic(() => import('@cvent/carina/components/Checkbox/CheckboxGroup'));

type Props = {
  title: string;
  ariaLabel: string;
  options: VenueDetailsOptions;
  selected: Filter;
  setSelected: (selections: Filter) => void;
  testID?: string;
};

export default function VenueDetailsFilter({ title, ariaLabel, options, selected, setSelected, testID }: Props) {
  const triggerFastResponderPopperRef = useRef<HTMLDivElement | null>(null);
  const triggerSocialTablesPopperRef = useRef<HTMLDivElement | null>(null);
  const triggerInstantBookPopperRef = useRef<HTMLDivElement | null>(null);
  const triggerPasskeyPopperRef = useRef<HTMLDivElement | null>(null);

  const [isFastResponderPopperOpen, setIsFastResponderPopperOpen] = useState(false);
  const [isSocialTablesPopperOpen, setIsSocialTablesPopperOpen] = useState(false);
  const [isInstantBookPopperOpen, setIsInstantBookPopperOpen] = useState(false);
  const [isPasskeyPopperOpen, setIsPasskeyPopperOpen] = useState(false);

  const toggleFastResponderPopper = useCallback(
    e => {
      setIsFastResponderPopperOpen(!isFastResponderPopperOpen);
      e.preventDefault();
    },
    [isFastResponderPopperOpen]
  );

  const onFastResponderPopperDismiss = useCallback(() => {
    setIsFastResponderPopperOpen(false);
  }, []);

  const toggleSocialTablesPopper = useCallback(
    e => {
      setIsSocialTablesPopperOpen(!isSocialTablesPopperOpen);
      e.preventDefault();
    },
    [isSocialTablesPopperOpen]
  );
  const onSocialTablesPopperDismiss = useCallback(() => {
    setIsSocialTablesPopperOpen(false);
  }, []);

  const toggleInstantBookPopper = useCallback(
    e => {
      setIsInstantBookPopperOpen(!isInstantBookPopperOpen);
      e.preventDefault();
    },
    [isInstantBookPopperOpen]
  );
  const onInstantBookPopperDismiss = useCallback(() => {
    setIsInstantBookPopperOpen(false);
  }, []);

  const togglePasskeyPopper = useCallback(
    e => {
      setIsPasskeyPopperOpen(!isPasskeyPopperOpen);
      e.preventDefault();
    },
    [isPasskeyPopperOpen]
  );
  const onPasskeyPopperDismiss = useCallback(() => {
    setIsPasskeyPopperOpen(false);
  }, []);

  const checkboxOptions: CheckboxGroupOption[] = useMemo(
    () =>
      options.map(option => ({
        label: (
          <div className="text-sm -mt-px flex h-5 font-normal">
            {option.label}

            {option.infoIcon && option.value === 'FAST_RESPONDER' && (
              <div
                role="button"
                tabIndex={0}
                className="ml-2.5 mt-px"
                onClick={toggleFastResponderPopper}
                ref={triggerFastResponderPopperRef}
                onKeyUp={e => {
                  if (e.key === 'Enter') {
                    toggleFastResponderPopper(e);
                  }
                }}
                aria-label={ariaLabel}
              >
                <Icon
                  id="info"
                  testID={`${testID}-${option.value}-information-icon`}
                  className="h-4 w-4 fill-neutral-60"
                />
                <Popper
                  aria-label="test popper"
                  className="rounded-xl"
                  isOpen={isFastResponderPopperOpen}
                  onDismiss={onFastResponderPopperDismiss}
                  triggerRef={triggerFastResponderPopperRef}
                  hasShadow
                >
                  <div className="normal inline-flex h-fit w-44 rounded-xl bg-background p-4 text-b-xs">
                    {option.infoIconContent}
                  </div>
                </Popper>
              </div>
            )}

            {option.infoIcon && option.value === 'SOCIAL_TABLES_ENABLED' && (
              <div
                role="button"
                tabIndex={0}
                className="ml-2.5 mt-px"
                onClick={toggleSocialTablesPopper}
                ref={triggerSocialTablesPopperRef}
                onKeyUp={e => {
                  if (e.key === 'Enter') {
                    toggleSocialTablesPopper(e);
                  }
                }}
                aria-label={ariaLabel}
              >
                <Icon
                  id="info"
                  testID={`${testID}-${option.value}-information-icon`}
                  className="h-4 w-4 fill-neutral-60"
                />
                <Popper
                  aria-label="test popper"
                  className="rounded-xl"
                  isOpen={isSocialTablesPopperOpen}
                  onDismiss={onSocialTablesPopperDismiss}
                  triggerRef={triggerSocialTablesPopperRef}
                  hasShadow
                >
                  <div className="normal inline-flex h-fit w-44 rounded-xl bg-background p-4 text-b-xs">
                    {option.infoIconContent}
                  </div>
                </Popper>
              </div>
            )}

            {option.infoIcon && option.value === 'INSTANT_BOOK_ENABLED' && (
              <div
                role="button"
                tabIndex={0}
                className="ml-2.5 mt-px"
                onClick={toggleInstantBookPopper}
                ref={triggerInstantBookPopperRef}
                onKeyUp={e => {
                  if (e.key === 'Enter') {
                    toggleInstantBookPopper(e);
                  }
                }}
                aria-label={ariaLabel}
              >
                <Icon
                  id="info"
                  testID={`${testID}-${option.value}-information-icon`}
                  className="h-4 w-4 fill-neutral-60"
                />
                <Popper
                  aria-label="test popper"
                  className="rounded-xl"
                  isOpen={isInstantBookPopperOpen}
                  onDismiss={onInstantBookPopperDismiss}
                  triggerRef={triggerInstantBookPopperRef}
                  hasShadow
                >
                  <div className="normal inline-flex h-fit w-44 rounded-xl bg-background p-4 text-b-xs">
                    {option.infoIconContent}
                  </div>
                </Popper>
              </div>
            )}
            {option.infoIcon && option.value === 'PASSKEY_ENABLED' && (
              <div
                role="button"
                tabIndex={0}
                className="ml-2.5 mt-px"
                onClick={togglePasskeyPopper}
                ref={triggerPasskeyPopperRef}
                onKeyUp={e => {
                  if (e.key === 'Enter') {
                    togglePasskeyPopper(e);
                  }
                }}
                aria-label={ariaLabel}
              >
                <Icon
                  id="info"
                  testID={`${testID}-${option.value}-information-icon`}
                  className="h-4 w-4 fill-neutral-60"
                />
                <Popper
                  aria-label="test popper"
                  className="rounded-xl"
                  isOpen={isPasskeyPopperOpen}
                  onDismiss={onPasskeyPopperDismiss}
                  triggerRef={triggerPasskeyPopperRef}
                  hasShadow
                >
                  <div className="normal inline-flex h-fit w-44 rounded-xl bg-background p-4 text-b-xs">
                    {option.infoIconContent}
                  </div>
                </Popper>
              </div>
            )}
          </div>
        ),
        value: option.value
      })),
    [
      options,
      toggleFastResponderPopper,
      ariaLabel,
      testID,
      isFastResponderPopperOpen,
      onFastResponderPopperDismiss,
      toggleSocialTablesPopper,
      isSocialTablesPopperOpen,
      onSocialTablesPopperDismiss,
      toggleInstantBookPopper,
      isInstantBookPopperOpen,
      onInstantBookPopperDismiss,
      togglePasskeyPopper,
      isPasskeyPopperOpen,
      onPasskeyPopperDismiss
    ]
  );
  const onUpdate = useCallback(
    selections => {
      const isOutdoorSpaceSelected = selections.includes('OUTDOOR_SPACE');
      const amenities = selected?.AMENITIES || [];
      let updatedAmenities = amenities;
      if (!isOutdoorSpaceSelected && amenities.includes('OUTDOOR_SPACE')) {
        updatedAmenities = amenities.filter(item => item !== 'OUTDOOR_SPACE');
      }
      if (isOutdoorSpaceSelected && !amenities.includes('OUTDOOR_SPACE')) {
        updatedAmenities = amenities?.concat('OUTDOOR_SPACE');
      }
      setSelected({
        ...selected,
        VENUE_DETAILS: selections,
        AMENITIES: updatedAmenities
      });
    },
    [selected, setSelected]
  );
  return (
    <div className="border-y border-neutral-20 py-3">
      <div className="mb-2 text-b-xl/loose font-medium" aria-label="venue-details-filter-title">
        {title}
      </div>
      <CheckboxGroup
        name="venue_details_options"
        labelText=""
        testID={`${testID}-venue-details-options`}
        options={checkboxOptions}
        selected={selected.VENUE_DETAILS}
        onUpdate={onUpdate}
      />
    </div>
  );
}
